/**
 * /admin — Admin login (token only). On success, redirects to /admin/customers.
 * If already signed in, auto-redirects.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Eye, Loader2, ShieldAlert } from 'lucide-react';

import { adminApi } from '@/lib/api';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(true);

  // If already signed in, jump straight to the customer list.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await adminApi.sessionStatus();
        if (!cancelled) navigate('/admin/customers', { replace: true });
      } catch {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await adminApi.login(token.trim());
      navigate('/admin/customers');
    } catch (err) {
      setError(err?.detail || 'Sign-in failed.');
      setBusy(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B1930]">
        <Loader2 className="h-6 w-6 animate-spin text-[#C8A46B]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B1930] px-4 py-10">
      <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden">
        <div className="px-8 py-6 bg-gradient-to-br from-[#0B1930] to-[#111A2E] text-white">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#C8A46B]/20 border border-[#C8A46B]/40">
              <Eye className="h-5 w-5 text-[#C8A46B]" />
            </span>
            <div>
              <h1 className="font-heading uppercase tracking-wide text-xl">
                ZMEX Portal Admin
              </h1>
              <p className="text-xs text-white/70 mt-0.5">Support &amp; QA console</p>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="px-8 py-7 space-y-5">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-[#4B5563] font-semibold">
              Admin token
            </span>
            <input
              type="password"
              autoComplete="off"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-[#EDE5D4] px-4 py-2.5 text-sm text-[#0B1930] focus:outline-none focus:border-[#C8A46B]"
              placeholder="paste PORTAL_ADMIN_TOKEN"
              data-testid="admin-login-token"
              autoFocus
              required
            />
          </label>

          {error && (
            <div
              className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700"
              data-testid="admin-login-error"
            >
              <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C8A46B] hover:bg-[#B8925A] text-[#0B1930] px-4 py-3 text-sm font-bold uppercase tracking-wide transition-colors disabled:opacity-60"
            data-testid="admin-login-submit"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            Sign in
          </button>

          <p className="text-[11px] text-center text-[#4B5563]">
            Session lasts 8 hours. All impersonations are audited.
          </p>
        </form>
      </div>
    </div>
  );
}
